'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { NewsItem } from '@glintfans/shared';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import { formatDateTime } from '@/lib/format';
import { ListSkeleton } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';

const PAGE_SIZE = 30;

interface NewsPageResp {
  items: NewsItem[];
  total: number;
}

export default function NewsPage() {
  const { language } = useAppStore();
  const t = translate(language);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [cat, setCat] = useState<string>('general');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sym, setSym] = useState('');
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // D22: 站内关键词搜索（防抖 350ms）
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['news', language, cat, debouncedQ, sym],
    queryFn: ({ pageParam }) =>
      api.get<NewsPageResp>(
        `/news?limit=${PAGE_SIZE}&offset=${pageParam}&language=${language}&category=${cat}` +
          (debouncedQ ? `&q=${encodeURIComponent(debouncedQ)}` : '') +
          (sym ? `&symbol=${sym}` : ''),
      ),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    refetchInterval: 5 * 60_000,
  });

  const zh = language !== 'en';

  // AUD18: strip HTML entities / zero-width chars / literal \uXXXX escapes
  // so titles & summaries never render raw markup or escaped keys.
  const clean = (s: string): string =>
    s
      .replace(/\\u([0-9a-fA-F]{4})/g, (_, h) => String.fromCharCode(parseInt(h, 16)))
      .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/[\u200b\u200c\u200d\ufeff]/g, '');

  // API already filters by language source; just pick the right title field
  const items = useMemo(
    () =>
      (data?.pages ?? []).flatMap((p) => p.items).map((n) => {
        const pick = language === 'zh-Hant' ? { title: n.zhTitle || n.title, summary: n.zhSummary || n.summary } : zh ? { title: n.title, summary: n.summary } : { title: n.enTitle || n.title, summary: n.enSummary || n.summary };
        return {
          ...n,
          title: clean(pick.title),
          summary: clean(pick.summary),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, zh],
  );

  const total = data?.pages[0]?.total ?? 0;

  // D22: 资产筛选下拉——从已加载资讯聚合出现过的关联币种
  const symOptions = useMemo(() => {
    const cnt = new Map<string, number>();
    for (const n of items) {
      for (const s of n.relatedSymbols ?? []) {
        cnt.set(s, (cnt.get(s) ?? 0) + 1);
      }
    }
    return [...cnt.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([s]) => s)
      .slice(0, 20);
  }, [items]);

  // D22: 分类未知给「综合」（general），兼容存量 latest 数据
  const CATS = ['general', 'crypto', 'stocks', 'ai', 'onchain'];

  const catLabel = (c: string): string => {
    const m: Record<string, string> = {
      general: language === 'zh-Hant' ? '綜合' : language !== 'en' ? '综合' : 'General',
      latest: language === 'zh-Hant' ? '最新' : language !== 'en' ? '综合' : 'General',
      crypto: language !== 'en' ? '加密' : 'Crypto',
      stocks: language !== 'en' ? '股票' : 'Stocks',
      ai: 'AI',
      onchain: language === 'zh-Hant' ? '鏈上' : language !== 'en' ? '链上' : 'On-chain',
    };
    return m[c] ?? (language === 'zh-Hant' ? '綜合' : language !== 'en' ? '综合' : 'General');
  };

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Infinite scroll observer -> load next page
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, items.length]);

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">{t.news.title}</h1>
      <div className="text-xs text-gf-muted">{language === 'zh-Hant' ? 'AiLumix 熱點一覽無遺' : language !== 'en' ? 'AiLumix 热点一览无遗' : 'AiLumix flash news at a glance'}</div>

      {/* D22: 站内搜索 + 关联资产筛选（同一行） */}
      <div className="mb-2 mt-3 flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={language === 'zh-Hant' ? '搜尋站內資訊關鍵詞…' : language !== 'en' ? '搜寻站内资讯关键词…' : 'Search news keywords…'}
          className="h-8 flex-1 rounded-md border border-gf-border bg-gf-surface2 px-3 text-xs text-gf-text outline-none placeholder:text-gf-muted focus:border-gf-accent min-w-[180px]"
        />
        <select
          value={sym}
          onChange={(e) => setSym(e.target.value)}
          className="h-8 rounded-md border border-gf-border bg-gf-surface2 px-2 text-xs text-gf-text outline-none focus:border-gf-accent"
        >
          <option value="">{language === 'zh-Hant' ? '全部資產' : language !== 'en' ? '全部资产' : 'All assets'}</option>
          {symOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-2 mt-3 flex gap-1 overflow-x-auto whitespace-nowrap pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
              cat === c
                ? 'border-gf-accent bg-gf-accent/10 text-gf-accent'
                : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
            }`}
          >
            {catLabel(c)}
          </button>
        ))}
      </div>

      {isLoading && <ListSkeleton rows={6} />}
      {isError && (
        <EmptyState
          error
          title={t.common.error}
          description={language === 'zh-Hant' ? '資訊載入失敗，請重試' : language !== 'en' ? '资讯加载失败，请重试' : 'Failed to load news. Please retry.'}
          actionLabel={t.common.retry}
          onAction={() => refetch()}
        />
      )}

      {!isLoading && !isError && (
        <div className="mx-auto mt-3 max-w-[960px]">
          {items.length === 0 && (
            <EmptyState
              compact
              title={t.news.empty}
              description={language === 'zh-Hant' ? '切換分類或關鍵字試試' : language !== 'en' ? '切换分类或关键字试试' : 'Try another category or keyword.'}
            />
          )}
          {items.map((n) => {
            const isOpen = expanded.has(n.id);
            const isClusterOpen = expanded.has(`${n.id}:updates`);
            // SUMMARY_LIMIT tuned for mobile: a ~90-char summary already wraps
            // past 3 lines on a 390px screen, so show the toggle for shorter text too.
            const showExpand = n.summary.length > 60;
            return (
              <div
                key={n.id}
                className="border-l-2 border-gf-border py-3 pl-4 hover:border-gf-accent"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs text-gf-muted">
                  <span title={new Date(n.publishedAt).toLocaleString('en-US', { timeZone: 'UTC' }) + ' UTC'}>
                    {formatDateTime(n.publishedAt)}
                  </span>
                  {n.category && n.category !== 'latest' && (
                    <span className="rounded bg-gf-surface2 px-1.5 py-0.5">{catLabel(n.category)}</span>
                  )}
                  {/* D22: 相似事件合并「更新 N 次」徽标 */}
                  {(n.clusterUpdates ?? 0) > 0 && (
                    <button
                      onClick={() => toggle(`${n.id}:updates`)}
                      className="rounded bg-gf-accent/10 px-1.5 py-0.5 font-medium text-gf-accent hover:underline"
                    >
                      {language !== 'en'
                        ? `更新 ${n.clusterUpdates} 次`
                        : `Updated ${n.clusterUpdates}x`}
                    </button>
                  )}
                  {/* D22: 关联币对仅站内行情链接 */}
                  {n.relatedSymbols && n.relatedSymbols.length > 0 && (
                    <span className="flex flex-wrap items-center gap-1">
                      {n.relatedSymbols.map((s) => (
                        <Link
                          key={s}
                          href={`/coin/${s}USDT?type=spot`}
                          className="rounded bg-gf-surface2 px-1.5 py-0.5 text-gf-accent hover:underline"
                        >
                          #{s}
                        </Link>
                      ))}
                    </span>
                  )}
                </div>
                <h2 className="mt-1.5 font-medium leading-snug line-clamp-2">{n.title}</h2>
                <p
                  className={`mt-1 text-sm text-gf-muted ${
                    !isOpen ? 'line-clamp-3 sm:line-clamp-2' : ''
                  }`}
                >
                  {n.summary}
                </p>
                {showExpand && (
                  <button
                    onClick={() => toggle(n.id)}
                    className="mt-1 text-xs text-gf-accent hover:underline"
                  >
                    {isOpen ? t.news.collapse : t.news.expand}
                  </button>
                )}
                {/* D22: 合并事件详情——展开显示各次更新（标题/时间/来源） */}
                {isClusterOpen && (n.clusterItems?.length ?? 0) > 0 && (
                  <div className="mt-2 space-y-1.5 rounded-md border border-gf-border bg-gf-surface2/50 p-2">
                    {n.clusterItems!.map((u, i) => (
                      <div key={`${n.id}-u${i}`} className="flex items-start gap-2 text-xs">
                        <span className="shrink-0 text-gf-muted">
                          {formatDateTime(u.publishedAt)}
                        </span>
                        <span className="min-w-0 flex-1 text-gf-text">{clean(u.title)}</span>
                        <span className="shrink-0 text-gf-muted">{u.source}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Load more trigger */}
          <div ref={loadMoreRef} className="py-6 text-center">
            {isFetchingNextPage && (
              <div className="text-sm text-gf-muted">
                {language === 'zh-Hant' ? '載入更多...' : language !== 'en' ? '载入更多...' : 'Loading more...'}
              </div>
            )}
            {!hasNextPage && items.length > 0 && (
              <div className="text-xs text-gf-muted">
                {language === 'zh-Hant' ? `已顯示全部 ${total} 條新聞` : language !== 'en' ? `已显示全部 ${total} 条新闻` : `All ${total} news loaded`}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
